import { SchemaTemplateOptionCard } from "./SchemaTemplateOptionCard";
import type { SchemaTemplate } from "@/types/schemaBuilder";
import { X } from "lucide-react";
import { useState } from "react";

interface SchemaTemplateSelectionModalProps {
  templates: SchemaTemplate[];
  selectedTemplate: SchemaTemplate | null;
  onSelectTemplate: (template: SchemaTemplate) => void;
  onClose: () => void;
  onConfirm: () => void;
}

export function SchemaTemplateSelectionModal({
  templates,
  selectedTemplate,
  onSelectTemplate,
  onClose,
  onConfirm,
}: SchemaTemplateSelectionModalProps) {
  const [localSelectedTemplate, setLocalSelectedTemplate] = useState<SchemaTemplate | null>(
    selectedTemplate
  );

  function handleConfirm() {
    if (!localSelectedTemplate) return;

    onSelectTemplate(localSelectedTemplate);
    onConfirm();
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="w-[860px] rounded-lg bg-white p-5 shadow-2xl">
        <div className="mb-5 flex items-start justify-between">
          <div>
            <h2 className="text-base font-semibold text-[var(--color-text-primary)]">
              Schema Template Options
            </h2>

            <p className="mt-3 max-w-[620px] text-xs leading-5 text-[var(--color-text-primary)]">
              Select the schema template that best suits your organization&apos;s needs. Once
              selected, you may customize that schema&apos;s columns and rules.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mb-4 flex items-center justify-end gap-3">
          <span className="text-xs text-[var(--color-text-primary)]">
            Showing {templates.length} of {templates.length}
          </span>
        </div>

        <div className="grid max-h-[520px] grid-cols-3 gap-5 overflow-y-auto pr-1">
          {templates.map((template) => (
            <SchemaTemplateOptionCard
              key={template.id}
              template={template}
              selected={localSelectedTemplate?.id === template.id}
              onClick={() => setLocalSelectedTemplate(template)}
            />
          ))}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-text-primary)]"
          >
            Back
          </button>

          <button
            type="button"
            onClick={handleConfirm}
            disabled={!localSelectedTemplate}
            className="rounded bg-[var(--color-action-primary)] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[var(--color-action-primary-hover)] disabled:bg-[var(--color-bg-secondary)] disabled:text-[var(--color-text-tertiary)]"
          >
            Confirm Selection
          </button>
        </div>
      </div>
    </div>
  );
}
